import { _ as __decorate, a as __metadata, B as BaseComponent } from '../index-gIJdRrzO.js';
import { css, html } from 'lit';
import { property, state, queryAssignedElements, customElement } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'lit/directive.js';

var styles = css`@charset "UTF-8";
*,
*::before,
*::after {
  box-sizing: border-box;
}

@media (prefers-reduced-motion: no-preference) {
  :root {
    scroll-behavior: smooth;
  }
}

body {
  margin: 0;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
}

hr {
  margin: 1rem 0;
  color: inherit;
  border: 0;
  border-top: 1px solid;
  opacity: 0.25;
}

p {
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
}

abbr[title] {
  text-decoration: underline dotted;
  cursor: help;
  text-decoration-skip-ink: none;
}

address {
  margin-bottom: var(--bsi-spacing-s);
  font-style: normal;
  line-height: inherit;
}

ol,
ul {
  padding-left: var(--bsi-spacing-l);
}

ol,
ul,
dl {
  margin-top: 0;
  margin-bottom: var(--bsi-spacing-s);
}

ol ol,
ul ul,
ol ul,
ul ol {
  margin-bottom: 0;
}

dt {
  font-weight: var(--bsi-font-weight-strong);
}

dd {
  margin-bottom: var(--bsi-spacing-xxs);
  margin-left: 0;
}

blockquote {
  margin: 0 0 var(--bsi-spacing-s);
}

sub,
sup {
  position: relative;
  font-size: var(--bsi-font-size-1);
  line-height: 0;
  vertical-align: baseline;
}

sub {
  bottom: -0.25em;
}

sup {
  top: -0.5em;
}

a {
  color: var(--bsi-color-link);
  text-decoration: underline;
  text-decoration-skip-ink: auto;
  text-underline-offset: 2px;
}
a:hover {
  color: var(--bsi-color-link-hover);
  cursor: pointer;
}

pre,
code,
kbd,
samp {
  font-size: 1em;
}

pre {
  display: block;
  margin-top: 0;
  margin-bottom: var(--bsi-paragraph-spacing);
  overflow: auto;
}
pre code {
  word-break: normal;
}

a > code {
  color: inherit;
}

figure {
  margin: 0 0 1rem;
}

img,
svg {
  vertical-align: middle;
}

table {
  caption-side: bottom;
  border-collapse: collapse;
}

caption {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  color: hsl(210, 17%, 44%);
  text-align: left;
}

th {
  text-align: inherit;
  text-align: -webkit-match-parent;
}

thead,
tbody,
tfoot,
tr,
td,
th {
  border-color: inherit;
  border-style: solid;
  border-width: 0;
}

label {
  display: inline-block;
}

button {
  border-radius: 0;
}

button:focus:not(:focus-visible) {
  outline: 0;
}

input,
button,
select,
optgroup,
textarea {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

button,
select {
  text-transform: none;
}

[role=button] {
  cursor: pointer;
}

select {
  word-wrap: normal;
}
select:disabled {
  opacity: 1;
}

[list]:not([type=date]):not([type=datetime-local]):not([type=month]):not([type=week]):not([type=time])::-webkit-calendar-picker-indicator {
  display: none !important;
}

button,
[type=button],
[type=reset],
[type=submit] {
  -webkit-appearance: button;
}
button:not(:disabled),
[type=button]:not(:disabled),
[type=reset]:not(:disabled),
[type=submit]:not(:disabled) {
  cursor: pointer;
}

::-moz-focus-inner {
  padding: 0;
  border-style: none;
}

textarea {
  resize: vertical;
}

fieldset {
  min-width: 0;
  padding: 0;
  margin: 0;
  border: 0;
}

::-webkit-datetime-edit-fields-wrapper,
::-webkit-datetime-edit-text,
::-webkit-datetime-edit-minute,
::-webkit-datetime-edit-hour-field,
::-webkit-datetime-edit-day-field,
::-webkit-datetime-edit-month-field,
::-webkit-datetime-edit-year-field {
  padding: 0;
}

::-webkit-inner-spin-button {
  height: auto;
}

[type=search] {
  outline-offset: -2px;
  -webkit-appearance: textfield;
}

/* rtl:raw:
[type="tel"],
[type="url"],
[type="email"],
[type="number"] {
  direction: ltr;
}
*/
::-webkit-search-decoration {
  -webkit-appearance: none;
}

::-webkit-color-swatch-wrapper {
  padding: 0;
}

::file-selector-button {
  font: inherit;
  -webkit-appearance: button;
}

output {
  display: inline-block;
}

iframe {
  border: 0;
}

summary {
  display: list-item;
  cursor: pointer;
}

progress {
  vertical-align: baseline;
}

[hidden] {
  display: none !important;
}

.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

:root {
  --bsi-form-control-height: 2.5rem;
  --bsi-form-control-spacing: var(--bsi-spacing-xxs);
  --bsi-form-control-background-color: var(--bsi-color-background-inverse);
  --bsi-form-control-border-color: var(--bsi-color-border-secondary);
  --bsi-form-control-border-radius: var(--bsi-radius-smooth);
  --bsi-form-control-placeholder-color: var(--bsi-color-text-muted);
  --bsi-form-control-label-color: var(--bsi-color-text-base);
  --bsi-form-control-text-color: var(--bsi-color-text-secondary);
  --bsi-form-control-font-size: var(--bsi-body-font-size);
  --bsi-form-group-spacing-y: var(--bsi-spacing-m);
  --bsi-form-checkbox-border-color: var(--bsi-color-border-secondary);
  --bsi-form-checkbox-border-radius: var(--bsi-radius-smooth);
  --bsi-form-checked-color: var(--bsi-color-background-primary);
}

input[readonly],
textarea[readonly],
select[readonly] {
  --bsi-form-control-border-color: var(--bsi-color-border-subtle);
  --bsi-form-control-background-color: var(--bsi-color-background-muted);
  cursor: not-allowed;
}

input,
textarea,
select {
  display: block;
  width: 100%;
  padding: var(--bsi-form-control-spacing);
  border: 1px solid var(--bsi-form-control-border-color);
  border-radius: var(--bsi-form-control-border-radius);
  background-color: var(--bsi-form-control-background-color);
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-form-control-font-size);
}
input.disabled, input:disabled,
textarea.disabled,
textarea:disabled,
select.disabled,
select:disabled {
  border-color: var(--bsi-color-border-disabled);
  opacity: 1;
  background-color: var(--bsi-color-background-disabled);
  color: var(--bsi-color-text-disabled);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input:focus,
textarea:focus {
  outline: 3px solid transparent;
  outline-offset: 3px;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
}

input::file-selector-button {
  margin: -0.375rem -0.75rem;
  padding: 0.375rem 0.75rem;
  border-width: 0;
  border-style: solid;
  border-radius: 0;
  border-color: inherit;
  color: hsl(0, 0%, 10%);
  pointer-events: none;
  margin-inline-end: 0.75rem;
  border-inline-end-width: 0;
  background-color: hsl(0, 0%, 100%);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  input::file-selector-button {
    transition: none;
  }
}
input:hover:not(:disabled):not([readonly])::file-selector-button {
  background-color: rgb(242.25, 242.25, 242.25);
}
input[type=file] {
  overflow: hidden;
}
input[type=file]:not(:disabled):not([readonly]) {
  cursor: pointer;
}
input::-webkit-date-and-time-value {
  height: 1.5em;
}

select {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='hsl%280, 0%, 32%%29' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right var(--bsi-form-control-spacing) center;
  background-size: 16px 12px;
  appearance: none;
}
select:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
select[multiple], select[size]:not([size="1"]) {
  padding-right: 0.75rem;
}
select:disabled {
  background-color: hsl(0, 0%, 90%);
}
select:disabled:hover {
  cursor: not-allowed;
}
select:-moz-focusring {
  color: transparent;
  text-shadow: 0 0 0 hsl(0, 0%, 10%);
}
select option {
  font-weight: normal;
}
select {
  /* stylelint-disable-next-line no-duplicate-selectors */ /* TO DO check these styles */
}
select:disabled {
  opacity: 1;
  background-color: hsl(210, 3%, 85%);
}

textarea {
  height: auto;
  font-size: var(--bsi-body-font-size);
  line-height: 1.5;
}

label {
  display: inline-block;
  width: auto;
  max-width: 100%;
  margin-bottom: var(--bsi-spacing-xxs);
  color: var(--bsi-form-control-label-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
  line-height: var(--bsi-label-leading);
}

/* stylelint-disable-next-line no-duplicate-selectors */
input,
textarea {
  outline: 0;
  box-shadow: none;
  transition: none;
  appearance: none;
}

input[type=date],
input[type=datetime-local],
input[type=time] {
  display: flex;
}

fieldset legend {
  margin-bottom: var(--bsi-spacing-s);
  padding: 0 var(--bsi-form-input-spacing-x);
  background-color: transparent;
  color: var(--bsi-form-control-text-color);
  font-size: var(--bsi-label-font-size-s);
  font-weight: var(--bsi-font-weight-solid);
}

::placeholder {
  color: var(--bsi-form-control-placeholder-color);
}

input::-webkit-datetime-edit {
  background-color: var(--bsi-color-background-primary-lighter);
  color: var(--bsi-form-contro-text-color);
}

.form-group {
  position: relative;
  margin-bottom: var(--bsi-form-group-spacing-y);
}
.form-group label.input-symbol-label:not(.active) {
  left: 2.25rem;
}
.form-group small.form-text {
  margin: 0;
  font-size: 0.875rem;
}
.form-group input[type=time] ~ label {
  font-size: 0.875rem;
}

.form-text {
  margin: var(--bsi-spacing-xxs) 0;
  color: var(--bsi-color-text-secondary);
}

.form-group.active .form-file-name {
  padding-bottom: 1.95rem;
}

.warning-feedback {
  display: none;
  width: 100%;
  margin-top: 0.25rem;
  color: var(--bsi-color-text-warning);
  font-size: 0.75rem;
}

.valid-feedback,
.invalid-feedback,
.warning-feedback {
  margin-left: 0.5rem;
}

.input-group .input-group-text .btn {
  border-radius: var(--bsi-form-control-border-radius) 0 0 var(--bsi-form-control-border-radius);
}
.input-group .input-group-append {
  margin-left: 0;
}
.input-group .input-group-append .btn {
  height: 100%;
  padding-top: 0;
  padding-bottom: 0;
  border-bottom: 1px solid hsl(210, 17%, 44%);
  border-radius: 0 var(--bsi-form-control-border-radius) var(--bsi-form-control-border-radius) 0;
}

.form-check {
  position: relative;
}
.form-check + .form-check {
  margin-top: var(--bsi-spacing-s);
}
.form-check [type=checkbox],
.form-check [type=radio] {
  position: absolute;
  top: 9px;
  left: 9px;
  width: auto;
  margin-top: 0;
  margin-left: 0;
  opacity: 0;
}
.form-check [type=checkbox] + label,
.form-check [type=radio] + label {
  position: relative;
  margin-bottom: 0;
  padding-left: 28px;
  font-size: var(--bsi-label-font-size);
  font-weight: var(--bsi-font-weight-solid);
  cursor: pointer;
  user-select: none;
}
@media (min-width: 576px) {
  .form-check [type=checkbox] + label,
  .form-check [type=radio] + label {
    font-size: var(--bsi-label-font-size-l);
  }
}
.form-check [type=checkbox]:focus + label,
.form-check [type=radio]:focus + label {
  border-color: hsl(0, 0%, 0%) !important;
  box-shadow: 0 0 0 2px var(--bsi-color-border-inverse), 0 0 0 5px var(--bsi-color-outline-focus) !important;
  outline: 3px solid transparent !important;
  outline-offset: 3px !important;
}
.form-check [type=checkbox]:focus[data-focus-mouse=true] + label,
.form-check [type=radio]:focus[data-focus-mouse=true] + label {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}
.form-check input[type=checkbox] + label::after,
.form-check input[type=checkbox] + label::before {
  position: absolute;
  left: 0;
  z-index: 1;
  content: "";
  border-width: 2px;
  border-style: solid;
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=checkbox] + label::after {
  top: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--bsi-form-control-border-radius);
}
.form-check input[type=checkbox]:checked + label::before {
  top: 3px;
  left: 3px;
  width: 6px;
  height: 12px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent var(--bsi-color-border-inverse) var(--bsi-color-border-inverse) transparent;
  opacity: 1;
  transform: rotate(40deg);
  transform-origin: 100% 100%;
  backface-visibility: hidden;
}
.form-check input[type=checkbox]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}
.form-check input[type=checkbox]:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checkbox-border-color);
  background-color: transparent;
}
.form-check input[type=checkbox]:not(:checked) + label::before {
  top: 10px;
  left: 6px;
  width: 0;
  height: 0;
  border-color: transparent;
}
.form-check input[type=checkbox]:disabled + label {
  opacity: 1;
  cursor: not-allowed;
}
.form-check input[type=checkbox]:disabled:not(:checked) + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: transparent;
}
.form-check input[type=checkbox]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio] + label::after, .form-check input[type=radio] + label::before {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  content: "";
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-radius: var(--bsi-radius-rounded);
  transition: all var(--bsi-transition-instant) ease-out;
}
.form-check input[type=radio]:not(:checked) + label::after, .form-check input[type=radio]:not(:checked) + label::before {
  border-color: var(--bsi-form-checkbox-border-color);
}
.form-check input[type=radio]:not(:checked) + label:after {
  z-index: -1;
  transform: scale(0);
}
.form-check input[type=radio]:checked + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
  transform: scale(0.64);
}
.form-check input[type=radio]:checked + label::before {
  border-color: var(--bsi-form-checked-color);
}
.form-check input[type=radio]:disabled + label {
  cursor: not-allowed;
}
.form-check input[type=radio]:disabled:not(:checked) + label::after, .form-check input[type=radio]:disabled:not(:checked) + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::after {
  border-color: var(--bsi-color-border-disabled);
  background-color: var(--bsi-color-border-disabled);
}
.form-check input[type=radio]:disabled:checked + label::before {
  border-color: var(--bsi-color-border-disabled);
}
.form-check .form-text {
  display: block;
  margin-bottom: 0.5rem;
  padding-right: 3.25rem;
}
.form-check.form-check-group {
  margin-bottom: 1rem;
  padding: 0 0 1rem 0;
  box-shadow: inset 0 -1px 0 0 rgba(1, 1, 1, 0.1);
}
.form-check.form-check-group input[type=checkbox] + label,
.form-check.form-check-group input[type=radio] + label {
  position: static;
  padding-right: 3.25rem;
  padding-left: 0;
}
.form-check.form-check-group input[type=checkbox] + label::after, .form-check.form-check-group input[type=checkbox] + label::before,
.form-check.form-check-group input[type=radio] + label::after,
.form-check.form-check-group input[type=radio] + label::before {
  right: 0;
  left: auto;
}
.form-check.form-check-group input[type=checkbox]:checked + label::before {
  right: 11px;
}
.form-check.form-check-group input[type=radio]:checked + label::before {
  right: 0;
}
.form-check.form-check-group input.semi-checked:not(:checked) + label::before {
  right: 4px;
  left: auto;
}
.form-check input.semi-checked:not(:checked) + label::before {
  top: 12px;
  left: 4px;
  width: 12px;
  height: 2px;
  border-width: 0;
  border-style: none;
  border-color: transparent;
  opacity: 1;
  background: var(--bsi-color-background-inverse);
  transform: none;
  backface-visibility: hidden;
}
.form-check input.semi-checked:not(:checked) + label::after {
  z-index: 0;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
}

.form-check-inline {
  display: inline-block;
}
.form-check-inline:not(:last-child) {
  margin-right: var(--bsi-spacing-m);
}

@media (prefers-reduced-motion: reduce) {
  fieldset legend,
  .form-group label,
  textarea,
  .form-check [type=checkbox],
  .form-check [type=radio],
  .form-check [type=checkbox] + label::after,
  .form-check [type=checkbox] + label::before,
  .form-check [type=radio] + label::after,
  .form-check [type=radio] + label::before,
  .toggles label input[type=checkbox] + .lever::before,
  .toggles label input[type=checkbox] + .lever::after {
    transition: none !important;
  }
}
.form-control-plaintext {
  border: 0;
  --bsi-form-control-border-color: transparent;
  --bsi-form-control-border-radius: 0;
  --bsi-form-control-background-color: transparent;
  --bsi-form-control-spacing: 0;
}
.form-control-plaintext:focus {
  outline: 0;
  box-shadow: none !important;
}
.form-control-plaintext + label {
  cursor: text;
}

.form-control {
  background-repeat: no-repeat;
  background-position: center right;
  background-size: 45px 30%;
}
.form-control:disabled {
  cursor: not-allowed;
  background: var(--bsi-color-background-disabled);
  border: 0;
  color: var(--bsi-color-text-disabled);
}
.was-validated .form-control:valid, .form-control.is-valid {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23008055' viewBox='0 0 192 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
}
.was-validated .form-control:invalid, .form-control.is-invalid {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23cc334d' viewBox='0 0 384 512'%3E%3Cpath d='M231.6 256l130.1-130.1c4.7-4.7 4.7-12.3 0-17l-22.6-22.6c-4.7-4.7-12.3-4.7-17 0L192 216.4 61.9 86.3c-4.7-4.7-12.3-4.7-17 0l-22.6 22.6c-4.7 4.7-4.7 12.3 0 17L152.4 256 22.3 386.1c-4.7 4.7-4.7 12.3 0 17l22.6 22.6c4.7 4.7 12.3 4.7 17 0L192 295.6l130.1 130.1c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17L231.6 256z'/%3E%3C/svg%3E");
  border-color: var(--bsi-color-border-danger);
}
.form-control.warning {
  background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%0A%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M2%2012C2%206.47715%206.47715%202%2012%202C14.6522%202%2017.1957%203.05357%2019.0711%204.92893C20.9464%206.8043%2022%209.34784%2022%2012C22%2017.5228%2017.5228%2022%2012%2022C6.47715%2022%202%2017.5228%202%2012ZM3%2012C3%2016.9706%207.02944%2021%2012%2021C16.9706%2021%2021%2016.9706%2021%2012C21%207.02944%2016.9706%203%2012%203C7.02944%203%203%207.02944%203%2012ZM11.5%2014.2V5.7H12.7V14.2H11.5ZM12.6%2018.3V16.5H11.4V18.3H12.6Z%22/%3E%0A%3C/svg%3E") no-repeat;
  border-color: var(--bsi-color-border-warning);
}
.form-control.is-valid ~ .warning-feedback {
  display: block;
}

.form-control-sm {
  --bsi-form-control-spacing: var(--bsi-spacing-xxs) var(--bsi-spacing-3xs);
  --bsi-form-control-font-size: var(--bsi-label-font-size);
}
.form-control-sm::file-selector-button {
  padding: 0.25rem 0.5rem;
  margin: -0.25rem -0.5rem;
  margin-inline-end: 0.5rem;
}

.form-control-lg {
  --bsi-form-control-font-size: var(--bsi-lead-font-size);
}
.form-control-lg::file-selector-button {
  padding: 0.5rem 1rem;
  margin: -0.5rem -1rem;
  margin-inline-end: 1rem;
}

textarea.form-control {
  min-height: 2.5rem;
  border: 1px solid hsl(210, 17%, 44%);
}
textarea.form-control-sm {
  min-height: calc(1.5em + 0.5rem);
}
textarea.form-control-lg {
  min-height: calc(1.5em + 1rem);
}

.form-control-color {
  width: 3rem;
  height: 2.5rem;
  padding: 0.375rem;
}
.form-control-color:not(:disabled):not([readonly]) {
  cursor: pointer;
}
.form-control-color::-moz-color-swatch {
  border: 0 !important;
  border-radius: 0;
}
.form-control-color::-webkit-color-swatch {
  border-radius: 0;
}
.form-control-color.form-control-sm {
  height: calc(1.5em + 0.5rem);
}
.form-control-color.form-control-lg {
  height: calc(1.5em + 1rem);
}

.form-check-reverse {
  padding-right: 1.5em;
  padding-left: 0;
  text-align: right;
}
.form-check-reverse .form-check-input {
  float: right;
  margin-right: -1.5em;
  margin-left: 0;
}

.form-check-input {
  width: 1em;
  height: 1em;
  margin-top: 0.25em;
  vertical-align: top;
  background-color: hsl(0, 0%, 100%);
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: 1px solid rgba(0, 0, 0, 0.25);
  appearance: none;
  print-color-adjust: exact;
}
.form-check-input[type=checkbox] {
  border-radius: 0.25em;
}
.form-check-input[type=radio] {
  border-radius: 50%;
}
.form-check-input:active {
  filter: brightness(90%);
}
.form-check-input:focus {
  border-color: hsl(210, 17%, 44%);
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(0, 102, 204, 0.25);
}
.form-check-input:checked {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
}
.form-check-input:checked[type=checkbox] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/></svg>"));
}
.form-check-input:checked[type=radio] {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='2' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-check-input[type=checkbox]:indeterminate {
  background-color: hsl(210, 100%, 40%);
  border-color: hsl(210, 100%, 40%);
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='none' stroke='hsl(0, 0%, 100%)' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/></svg>"));
}
.form-check-input:disabled {
  pointer-events: none;
  filter: none;
  opacity: 0.5;
}
.form-check-input[disabled] ~ .form-check-label, .form-check-input:disabled ~ .form-check-label {
  cursor: default;
  opacity: 0.5;
}

.form-switch {
  padding-left: 2.5em;
}
.form-switch .form-check-input {
  width: 2em;
  margin-left: -2.5em;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='rgba(0, 0, 0, 0.25)'/></svg>"));
  background-position: left center;
  border-radius: 2em;
  transition: background-position 0.15s ease-in-out;
}
@media (prefers-reduced-motion: reduce) {
  .form-switch .form-check-input {
    transition: none;
  }
}
.form-switch .form-check-input:focus {
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(210, 17%, 44%)'/></svg>"));
}
.form-switch .form-check-input:checked {
  background-position: right center;
  background-image: escape-svg(url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'><circle r='3' fill='hsl(0, 0%, 100%)'/></svg>"));
}
.form-switch.form-check-reverse {
  padding-right: 2.5em;
  padding-left: 0;
}
.form-switch.form-check-reverse .form-check-input {
  margin-right: -2.5em;
  margin-left: 0;
}

.form-check-inline {
  display: inline-block;
  margin-right: 1rem;
}

.btn-check {
  position: absolute;
  clip: rect(0, 0, 0, 0);
  pointer-events: none;
}
.btn-check[disabled] + .btn, .btn-check:disabled + .btn {
  pointer-events: none;
  filter: none;
  opacity: 0.65;
}

.form-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
}
.form-feedback.just-validate-error-label {
  color: var(--bsi-color-text-danger);
}

.input-group-text:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) ~ .input-group-text,
button:has(~ [data-focus-mouse=true]:not(.btn)),
[data-focus-mouse=true]:not(.btn) + button {
  border-color: inherit !important;
  box-shadow: none !important;
  outline: none !important;
}

.input-group-text:has(~ .is-invalid),
.is-invalid ~ .input-group-text,
button:has(~ .is-invalid),
.is-invalid + button {
  border-color: var(--bsi-color-border-danger) !important;
}

.sr-only-justvalidate-bi {
  display: none;
}

.just-validate-success-field {
  border-color: var(--bsi-color-border-success) !important;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%23008055' viewBox='0 0 192 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
}

.input-group-text:has(~ .just-validate-success-field),
.just-validate-success-field ~ .input-group-text,
button:has(~ .just-validate-success-field),
.just-validate-success-field + button {
  border-color: var(--bsi-color-border-success);
}

.just-validate-success-field + .input-group-text.align-buttons,
.is-invalid + .input-group-text.align-buttons {
  right: 30px;
}

.is-invalid + .input-group-text.align-buttons {
  bottom: 22px;
}

.autocomplete__wrapper .form-feedback.just-validate-error-label {
  position: absolute;
}

textarea.form-control {
  background-position: top 0.3em right 0.3em !important;
  background-size: 37px 30% !important;
}
textarea.is-invalid {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}
textarea.just-validate-success-field {
  border-bottom-style: solid;
  border-bottom-width: 1px;
}

input[type=checkbox].is-invalid,
input[type=radio].is-invalid {
  --bsi-form-checkbox-border-color: var(--bsi-color-border-danger);
}

select.is-invalid {
  border: 1px solid var(--bsi-color-border-danger);
}
select.just-validate-success-field {
  border: 1px solid var(--bsi-color-border-success);
}

:host {
  display: block;
  margin-bottom: var(--bsi-spacing-xs);
  box-shadow: none !important;
  -webkit-focus-ring-color: transparent; /* Chrome/Safari */
  outline: none !important;
}

/* Mostra il ring sul controllo interno in questi casi:
   - host stesso è :focus-visible (keyboard)
   - host contiene il focus (qualunque elemento figlio sia focalizzato)
   - fallback: anche quando host ha semplicemente :focus (utile per alcuni casi) */
:host(:focus-visible) .radio-control {
  box-shadow: 0 0 0 2px hsl(0, 0%, 100%), 0 0 0 5px hsl(0, 0%, 0%) !important;
  /* assicurati che il controllo non abbia outline propio che interferisca */
  outline: none;
}

.radio-control {
  display: flex;
  width: fit-content;
  align-items: center;
  padding: var(--bsi-form-input-padding-y) var(--bsi-form-input-padding-x);
  cursor: pointer;
}

.radio-control-label {
  display: block;
  width: 100%;
  color: var(--it-radio-label-color, var(--bsi-form-control-label-color));
  font-size: var(--bsi-label-sm);
  font-weight: var(--bsi-font-weight-solid);
}
@media (min-width: 576px) {
  .radio-control-label {
    font-size: var(--bsi-label-font-size-m);
  }
}

.radio-control-grouped {
  width: 100%;
  flex-direction: row-reverse;
}

:host([inline]) {
  display: inline-block;
  margin-right: 1rem;
  margin-bottom: 0;
}

:host(:not([group])).form-check label {
  margin-bottom: var(--bsi-spacing-xxs);
}

#button {
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  border: none !important;
  margin-top: 0 !important;
  margin-right: 0.5rem;
}
#button::before {
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-width: 2px;
  border-style: solid;
  border-color: var(--bsi-form-control-label-color);
  border-radius: var(--bsi-radius-rounded);
  content: "";
  transition: all var(--bsi-transition-instant) ease-out;
}
#button::after {
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-width: 0;
  border-style: solid;
  border-color: transparent;
  border-radius: var(--bsi-radius-rounded);
  background-color: transparent;
  content: "";
  transform: scale(0);
  transition: all var(--bsi-transition-instant) ease-out;
}

:host(:not([aria-checked=true])) #button::before {
  border-color: var(--bsi-form-checkbox-border-color);
}
:host(:not([aria-checked=true])) #button::after {
  z-index: -1;
  border-width: 0;
  border-color: transparent;
  background-color: transparent;
  transform: scale(0);
}

:host([aria-checked=true]) #button::before {
  border-color: var(--bsi-form-checked-color);
}
:host([aria-checked=true]) #button::after {
  z-index: 0;
  border-width: 2px;
  border-color: var(--bsi-form-checked-color);
  background-color: var(--bsi-form-checked-color);
  transform: scale(0.64);
}

:host([aria-disabled=true]) .radio-control {
  cursor: not-allowed;
}
:host([aria-disabled=true]) #button::before,
:host([aria-disabled=true]) #button::after {
  border-color: var(--bsi-color-border-disabled);
}

:host([aria-disabled=true][aria-checked=true]) #button::after {
  background-color: var(--bsi-color-border-disabled);
}

:host([aria-invalid=true]) #button::before, :host([aria-invalid=true]) #button::after {
  border-color: var(--bsi-color-feedback-error);
}
:host([aria-invalid=true]) #button[aria-checked=true]::after {
  background-color: var(--bsi-color-feedback-error);
}

.form-text {
  display: block;
  margin-top: var(--bsi-spacing-xxs);
  margin-bottom: 0;
}`;

let ItRadio = class ItRadio extends BaseComponent {
    constructor() {
        super(...arguments);
        /** The radio's value attribute */
        this.value = '';
        /** Whether the radio is disabled */
        this.disabled = false;
        /** Draws the radio in a checked state. */
        this.checked = false;
        /** The input's help text. */
        this.supportText = '';
        /**
         * Internal reactive state synced from group
         * These replace the old getters that read from this.group
         */
        this._name = '';
        this._grouped = false;
        this._required = false;
        this._inline = false;
        /**
         * Handle keyup for Space key
         */
        this.handleKeyup = (event) => {
            if (event.code === 'Space') {
                event.preventDefault();
                this.activate(event);
            }
        };
        /**
         * Handle keydown - delegate arrow keys to the group
         */
        this.handleKeydown = (event) => {
            // Prevent Space from scrolling the page
            if (event.code === 'Space') {
                event.preventDefault();
                return;
            }
            // Let Tab and Shift+Tab work normally for navigation
            if (event.key === 'Tab') {
                this.group?.handleRadioKeyDown(this, event);
                return;
            }
            // Only prevent default for arrow keys (to prevent page scrolling)
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
                event.preventDefault();
            }
            // Delegate keyboard navigation to the group's public API
            this.group?.handleRadioKeyDown(this, event);
        };
        /**
         * Handle click on the host
         */
        this.handleClick = (event) => {
            this.activate(event);
        };
    }
    get label() {
        if (this.labelElements.length > 0) {
            return this.labelElements[0].innerText.trim();
        }
        return '';
    }
    get group() {
        return this.closest('it-radio-group');
    }
    // Public getters now read from internal state (reactive)
    get name() {
        return this._name || this.group?.name || '';
    }
    get grouped() {
        return this._grouped;
    }
    get required() {
        return this._required;
    }
    get inline() {
        return this._inline;
    }
    get invalid() {
        // Read aria-invalid explicitly from the group: only the string "true" should be considered invalid
        const aria = this.group?.getAttribute ? this.group.getAttribute('aria-invalid') : null;
        return aria === 'true';
    }
    /**
     * PUBLIC API: Called by group to sync state
     * This replaces the need for requestUpdate() calls
     */
    syncFromGroup(groupState) {
        if (groupState.name !== undefined)
            this._name = groupState.name;
        if (groupState.grouped !== undefined)
            this._grouped = groupState.grouped;
        if (groupState.required !== undefined)
            this._required = groupState.required;
        if (groupState.inline !== undefined)
            this._inline = groupState.inline;
    }
    /**
     * Activate the radio via the group's public API
     */
    activate(event) {
        if (this.checked || this.disabled) {
            return;
        }
        // Use the group's public API if available
        if (this.group) {
            this.group.selectRadio(this, event);
        }
        else {
            // Fallback if no group (shouldn't happen in normal usage)
            this.checked = true;
        }
    }
    connectedCallback() {
        super.connectedCallback?.();
        // Set role on host for screen reader context
        this.setAttribute('role', 'radio');
        // Set initial tabindex if not already set (roving tabindex will be managed by group)
        if (!this.hasAttribute('tabindex')) {
            this.tabIndex = 0;
        }
        // Add event listeners on host
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown, { capture: true });
        this.addEventListener('keyup', this.handleKeyup, { capture: true });
    }
    disconnectedCallback() {
        super.disconnectedCallback?.();
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown, { capture: true });
        this.removeEventListener('keyup', this.handleKeyup, { capture: true });
    }
    updated(changedProperties) {
        super.updated?.(changedProperties);
        // Update ARIA attributes on host
        if (changedProperties.has('checked')) {
            this.setAttribute('aria-checked', this.checked ? 'true' : 'false');
        }
        if (changedProperties.has('disabled')) {
            if (this.disabled) {
                this.setAttribute('aria-disabled', 'true');
            }
            else {
                this.removeAttribute('aria-disabled');
            }
        }
        // Sync name attribute from internal state
        if (changedProperties.has('_name')) {
            if (this._name) {
                this.setAttribute('name', this._name);
            }
            else {
                this.removeAttribute('name');
            }
        }
        // Sync aria-required from internal state
        if (changedProperties.has('_required')) {
            if (this._required) {
                this.setAttribute('aria-required', 'true');
            }
            else {
                this.removeAttribute('aria-required');
            }
        }
        // logger
        if (!this.label) {
            this.logger.warn(`Label is required to ensure accessibility. Please, define a label for <it-radio/> .`);
        }
    }
    /**
     * Render internal structure (like Spectrum)
     * Just visual elements, no role/aria/events here (they're on the host)
     * Protected to allow subclasses (like it-rating-item) to override
     */
    // eslint-disable-next-line class-methods-use-this
    _renderInput() {
        // Render visual elements like Spectrum does
        // #input = container, #button = visual circle
        const inputRender = html `
      <div id="input" part="input"></div>
      <span id="button" part="button" class="form-check-input"></span>
    `;
        return inputRender;
    }
    // Render the UI as a function of component state
    render() {
        const supportTextId = `${this._id}-support-text`;
        const supportTextRender = html ` ${when(this.supportText, () => html `<slot name="helpText"><small class="form-text" id="${supportTextId}">${this.supportText}</small></slot>`)}`;
        const wrapperClasses = this.composeClass('form-check', this.inline && !this.grouped ? 'form-check-inline' : '', this.grouped && !this.inline ? 'form-check-group' : '');
        const ariaDescribedBy = this.composeClass(this.supportText?.length > 0 ? supportTextId : '', this._ariaAttributes['aria-describedby']?.length > 0 ? this._ariaAttributes['aria-describedby'] : '');
        const controlClasses = this.composeClass('radio-control', this.grouped ? 'radio-control-grouped' : '');
        const labelClasses = this.composeClass('radio-control-label', this.disabled ? 'disabled' : '');
        return html `
      <div class="${wrapperClasses}" part="input-wrapper" aria-describedby="${ifDefined(ariaDescribedBy || undefined)}">
        <div id="radio-control" part="radio-control" class="${controlClasses}">
          ${this._renderInput()}
          <span part="label" class="${labelClasses}"><slot name="label">${this.label}</slot></span>
        </div>
        ${supportTextRender}
      </div>
    `;
    }
};
ItRadio.styles = styles;
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItRadio.prototype, "value", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadio.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItRadio.prototype, "checked", void 0);
__decorate([
    property({ type: String, attribute: 'support-text' }),
    __metadata("design:type", Object)
], ItRadio.prototype, "supportText", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItRadio.prototype, "_name", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItRadio.prototype, "_grouped", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItRadio.prototype, "_required", void 0);
__decorate([
    state(),
    __metadata("design:type", Object)
], ItRadio.prototype, "_inline", void 0);
__decorate([
    queryAssignedElements({ slot: 'label' }),
    __metadata("design:type", Array)
], ItRadio.prototype, "labelElements", void 0);
ItRadio = __decorate([
    customElement('it-radio')
], ItRadio);

export { ItRadio };
//# sourceMappingURL=it-radio.js.map
